import { Box } from "@mui/material";
import { TextField } from "@shared/form/TextField";
import { SectionField, SectionFieldRow } from "@shared/SectionPanel/SectionField";
import { Tooltip } from "@shared/Tooltip";

import { useStyles } from "./WorkspaceFormColumn.style";

export type EditableField<FieldName extends string> = {
    name: FieldName;
    label: string;
    placeholder?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    disabledTooltip?: string;
    /**
     * Custom read-only value (e.g. a link). Defaults to the form string value.
     */
    renderValue?: (value: string | undefined) => React.ReactNode;
};

export function displayOverviewValue(value: string | null | undefined) {
    return value?.trim() ? value : "-";
}

type FormColumnProps<FieldName extends string, Values extends Partial<Record<FieldName, string | undefined>>> = {
    fields: EditableField<FieldName>[];
    values: Values;
    editing: boolean;
    children?: React.ReactNode;
};

export function FormColumn<FieldName extends string, Values extends Partial<Record<FieldName, string | undefined>>>(
    props: FormColumnProps<FieldName, Values>,
) {
    const { classes, cx } = useStyles();

    return (
        <Box className={cx(classes.column, props.editing && classes.columnEditing)}>
            {props.fields.map((field) => {
                const rawValue = props.values[field.name];
                const displayValue = field.renderValue?.(rawValue) ?? displayOverviewValue(rawValue);

                return props.editing ? (
                    <SectionFieldRow key={field.name} label={field.label}>
                        <Box className={classes.inputSlot}>
                            <Tooltip title={field.disabledTooltip} hide={!field.disabled || !field.disabledTooltip}>
                                <TextField
                                    name={field.name}
                                    placeholder={field.placeholder}
                                    autoFocus={field.autoFocus}
                                    disabled={field.disabled}
                                    fullWidth
                                    config={{ parse: (value: string | undefined) => value ?? "" }}
                                    slotProps={{ htmlInput: { "aria-label": field.label } }}
                                />
                            </Tooltip>
                        </Box>
                    </SectionFieldRow>
                ) : (
                    <SectionField key={field.name} label={field.label} value={displayValue} />
                );
            })}
            {props.children}
        </Box>
    );
}
